import { useCallback, useEffect, useRef } from 'react';

interface UseIdleTimerHookProps {
  timeout: number; // in milliseconds
  timeToPromptBeforeTimeout?: number; // in milliseconds
  extendTime?: number; // in milliseconds
  isTimeExtended?: boolean;
  onTimeout: () => void;
  onTimeToPromptBeforeTimeout: () => void;
  isPrompted: boolean;
}

const useIdleTimerHook = ({
  timeout,
  timeToPromptBeforeTimeout = 30 * 1000,
  extendTime,
  isTimeExtended,
  onTimeout,
  onTimeToPromptBeforeTimeout,
  isPrompted,
}: UseIdleTimerHookProps) => {
  // Keep mutable timer state in refs so ticking does not re-render the app tree.
  const remainingTimeRef = useRef(timeout);
  const deadlineRef = useRef(Date.now() + timeout);
  const isTimeoutRef = useRef(false);
  const isReloadEligibleRef = useRef(false);
  const promptTimeRef = useRef(timeToPromptBeforeTimeout);
  const activeWindowRef = useRef(timeout);

  const callbacksRef = useRef({ onTimeout, onTimeToPromptBeforeTimeout });

  // Keep refs synchronized with the latest props/state.
  useEffect(() => {
    callbacksRef.current = { onTimeout, onTimeToPromptBeforeTimeout };
  }, [onTimeout, onTimeToPromptBeforeTimeout]);

  useEffect(() => {
    if (!isTimeExtended) {
      activeWindowRef.current = timeout;
    }
  }, [timeout, isTimeExtended]);

  useEffect(() => {
    promptTimeRef.current = timeToPromptBeforeTimeout;
  }, [timeToPromptBeforeTimeout]);

  // Handle resetting the timer on user activity.
  const handleUserActivity = useCallback((e: Event) => {
    if (isReloadEligibleRef.current) {
      return;
    }
    if (['click', 'touchstart', 'keydown'].includes(e.type)) {
      const nextTimeout = activeWindowRef.current;
      remainingTimeRef.current = nextTimeout;
      deadlineRef.current = Date.now() + nextTimeout;
      isTimeoutRef.current = false;
    }
  }, []);

  // Drive threshold transitions from interval checks without setting React state.
  useEffect(() => {
    const interval = setInterval(() => {
      const nextTime = Math.max(0, deadlineRef.current - Date.now());
      remainingTimeRef.current = nextTime;

      if (!isTimeoutRef.current && nextTime <= 0) {
        isTimeoutRef.current = true;
        callbacksRef.current.onTimeout();
        return;
      }

      if (
        !isPrompted &&
        !isReloadEligibleRef.current &&
        nextTime <= promptTimeRef.current
      ) {
        isReloadEligibleRef.current = true;
        callbacksRef.current.onTimeToPromptBeforeTimeout();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPrompted]);

  // Apply a one-time extension window when user chooses to stay.
  useEffect(() => {
    if (isTimeExtended && extendTime) {
      activeWindowRef.current = extendTime;
      remainingTimeRef.current = extendTime;
      deadlineRef.current = Date.now() + extendTime;
      isTimeoutRef.current = false;
      isReloadEligibleRef.current = false;
      return;
    }

    activeWindowRef.current = timeout;
  }, [isTimeExtended, extendTime, timeout]);

  // Keep prompt eligibility in sync with external prompt state.
  useEffect(() => {
    isReloadEligibleRef.current = isPrompted;
  }, [isPrompted]);

  // Global DOM event listeners for activity detection.
  useEffect(() => {
    const activityEvents = ['click', 'keydown', 'touchstart']; // 'touchstart' replaces non-standard 'tap'

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleUserActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  const getRemainingTime = useCallback(() => {
    remainingTimeRef.current = Math.max(0, deadlineRef.current - Date.now());
    return remainingTimeRef.current;
  }, []);

  return {
    getRemainingTime,
  };
};

export default useIdleTimerHook;
